import {
  collection,
  addDoc,
  getDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Create a new account
 * @param {string} userId - Owner user ID
 * @param {object} accountData - Account data
 * @param {string} accountData.companyName - Company name
 * @param {string} accountData.companyAddress - Company address
 * @param {string} accountData.companyPhone - Company phone
 * @param {string} accountData.companyEmail - Company email
 * @param {string} accountData.companyWebsite - Company website
 * @param {string} accountData.accountType - Account type (free, premium)
 * @returns {Promise<string>} - Account ID
 */
export const createAccount = async (userId, accountData = {}) => {
  try {
    const {
      companyName = '',
      companyAddress = '',
      companyPhone = '',
      companyEmail = '',
      companyWebsite = '',
      companyIndustry = '',
      companySize = '1-10',
      accountType = 'free'
    } = accountData;

    const docRef = await addDoc(collection(db, 'accounts'), {
      ownerId: userId,
      memberIds: [userId],
      companyName,
      companyAddress,
      companyPhone,
      companyEmail,
      companyWebsite,
      companyIndustry,
      companySize,
      accountType,
      dailyGenerationsLimit: accountType === 'premium' ? -1 : 1,
      scheduledPostsLimit: accountType === 'premium' ? -1 : 3,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating account:', error);
    throw new Error('Failed to create account');
  }
};

/**
 * Get account by ID
 * @param {string} accountId - Account document ID
 * @returns {Promise<object>} - Account data
 */
export const getAccount = async (accountId) => {
  try {
    const docRef = doc(db, 'accounts', accountId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Account not found');
    }

    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error getting account:', error);
    throw new Error('Failed to load account');
  }
};

/**
 * Get account for a user
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<object|null>} - Account data or null if not found
 */
export const getUserAccount = async (userId) => {
  try {
    const q = query(
      collection(db, 'accounts'),
      where('memberIds', 'array-contains', userId)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    // User should only be in one account
    const accountDoc = snapshot.docs[0];
    return { id: accountDoc.id, ...accountDoc.data() };
  } catch (error) {
    console.error('Error getting user account:', error);
    throw new Error('Failed to load user account');
  }
};

/**
 * Update account data
 * @param {string} accountId - Account document ID
 * @param {object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateAccount = async (accountId, updates) => {
  try {
    const accountRef = doc(db, 'accounts', accountId);
    await updateDoc(accountRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating account:', error);
    throw new Error('Failed to update account');
  }
};

/**
 * Get all members of an account
 * @param {string} accountId - Account document ID
 * @returns {Promise<Array>} - Array of user objects
 */
export const getAccountMembers = async (accountId) => {
  try {
    const q = query(
      collection(db, 'users'),
      where('accountId', '==', accountId)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting account members:', error);
    throw new Error('Failed to load account members');
  }
};

/**
 * Check if user has reached usage limits for their account
 * @param {string} accountId - Account document ID
 * @returns {Promise<object>} - Usage limits status
 */
export const checkUsageLimits = async (accountId) => {
  try {
    const account = await getAccount(accountId);

    return {
      accountType: account.accountType,
      dailyGenerationsLimit: account.dailyGenerationsLimit,
      scheduledPostsLimit: account.scheduledPostsLimit,
      hasUnlimitedGenerations: account.dailyGenerationsLimit === -1,
      hasUnlimitedScheduledPosts: account.scheduledPostsLimit === -1
    };
  } catch (error) {
    console.error('Error checking usage limits:', error);
    throw new Error('Failed to check usage limits');
  }
};

/**
 * Add a user to an account
 * @param {string} accountId - Account document ID
 * @param {string} userId - User ID to add
 * @returns {Promise<void>}
 */
export const addAccountMember = async (accountId, userId) => {
  try {
    const account = await getAccount(accountId);

    if (account.memberIds.includes(userId)) {
      throw new Error('User is already a member of this account');
    }

    const accountRef = doc(db, 'accounts', accountId);
    await updateDoc(accountRef, {
      memberIds: [...account.memberIds, userId],
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error adding account member:', error);
    throw new Error('Failed to add member to account');
  }
};

/**
 * Remove a user from an account
 * @param {string} accountId - Account document ID
 * @param {string} userId - User ID to remove
 * @returns {Promise<void>}
 */
export const removeAccountMember = async (accountId, userId) => {
  try {
    const account = await getAccount(accountId);

    if (account.ownerId === userId) {
      throw new Error('Cannot remove account owner');
    }

    const accountRef = doc(db, 'accounts', accountId);
    await updateDoc(accountRef, {
      memberIds: account.memberIds.filter(id => id !== userId),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error removing account member:', error);
    throw new Error('Failed to remove member from account');
  }
};

/**
 * Upgrade account to premium
 * @param {string} accountId - Account document ID
 * @returns {Promise<void>}
 */
export const upgradeAccountToPremium = async (accountId) => {
  try {
    await updateAccount(accountId, {
      accountType: 'premium',
      dailyGenerationsLimit: -1,
      scheduledPostsLimit: -1
    });
  } catch (error) {
    console.error('Error upgrading account:', error);
    throw new Error('Failed to upgrade account');
  }
};

/**
 * Downgrade account to free
 * @param {string} accountId - Account document ID
 * @returns {Promise<void>}
 */
export const downgradeAccountToFree = async (accountId) => {
  try {
    await updateAccount(accountId, {
      accountType: 'free',
      dailyGenerationsLimit: 1,
      scheduledPostsLimit: 3
    });
  } catch (error) {
    console.error('Error downgrading account:', error);
    throw new Error('Failed to downgrade account');
  }
};

/**
 * Update account company information
 * @param {string} accountId - Account document ID
 * @param {object} companyData - Company information to update
 * @param {string} companyData.companyName - Company name
 * @param {string} companyData.companyAddress - Company address
 * @param {string} companyData.companyPhone - Company phone
 * @param {string} companyData.companyEmail - Company email
 * @param {string} companyData.companyWebsite - Company website
 * @param {string} companyData.companyIndustry - Company industry
 * @param {string} companyData.companySize - Company size
 * @returns {Promise<void>}
 */
export const updateAccountCompanyInfo = async (accountId, companyData) => {
  try {
    const updates = {};

    // Only update fields that are provided
    if (companyData.companyName !== undefined) updates.companyName = companyData.companyName;
    if (companyData.companyAddress !== undefined) updates.companyAddress = companyData.companyAddress;
    if (companyData.companyPhone !== undefined) updates.companyPhone = companyData.companyPhone;
    if (companyData.companyEmail !== undefined) updates.companyEmail = companyData.companyEmail;
    if (companyData.companyWebsite !== undefined) updates.companyWebsite = companyData.companyWebsite;
    if (companyData.companyIndustry !== undefined) updates.companyIndustry = companyData.companyIndustry;
    if (companyData.companySize !== undefined) updates.companySize = companyData.companySize;

    await updateAccount(accountId, updates);
  } catch (error) {
    console.error('Error updating account company info:', error);
    throw new Error('Failed to update company information');
  }
};
